import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { useCallback, useState } from "react";

import { useSupercluster } from "@/hooks/useSupercluster";
import { EventMarker } from "components/CaseMap/EventMarker";
import { EventMarkerBadge } from "components/CaseMap/EventMarkerBadge";
import { abbreviateCount, flyTo } from "components/CaseMap/helpers";
import { Z_INDEX } from "components/CaseMap/mapLayers";
import { CENTERED_MARKER_ANCHOR } from "helpers/googleMaps";

// Events cluster more eagerly than machines: one machine can contribute
// thousands, and they carry no identity worth reading at a glance.
//
// A feature is one coordinate rather than one event, so the badge counts the
// events beneath it — point_count would report the number of places instead.
const superclusterOptions = {
  extent: 256,
  radius: 60,
  maxZoom: 16,
  map: ({ count }) => ({ count }),
  reduce: (accumulated, props) => {
    accumulated.count += props.count;
  },
};

/**
 * The events belonging to the machines under the overlay, as their own clustered
 * layer over the machine pins.
 *
 * The open summary is local state rather than store state: nothing outside this
 * layer needs to know which event is open, and the layer unmounts with the
 * overlay, which clears it.
 */
export function EventMarkers({ geojson }) {
  const map = useMap();
  const [openEventId, setOpenEventId] = useState(null);
  const { clusters, getClusterExpansionZoom } = useSupercluster(
    geojson,
    superclusterOptions
  );

  const handleClusterClick = useCallback(
    (clusterId, position) => {
      flyTo(map, { ...position, zoom: getClusterExpansionZoom(clusterId) });
    },
    [map, getClusterExpansionZoom]
  );

  // Toggling on a second click matches the machine pins, where clicking the open
  // pin dismisses its summary.
  const handleSelect = useCallback(
    eventId =>
      setOpenEventId(current => (current === eventId ? null : eventId)),
    []
  );

  const handleClose = useCallback(() => setOpenEventId(null), []);

  return clusters.map(feature => {
    const [lng, lat] = feature.geometry.coordinates;
    const position = { lat, lng };
    const { cluster, cluster_id, count, events } = feature.properties;

    if (cluster) {
      return (
        <AdvancedMarker
          key={`event-cluster-${cluster_id}`}
          position={position}
          zIndex={Z_INDEX.event}
          {...CENTERED_MARKER_ANCHOR}
          onClick={() => handleClusterClick(cluster_id, position)}
        >
          <EventMarkerBadge label={abbreviateCount(count)} />
        </AdvancedMarker>
      );
    }

    // The stack is identified by its newest event, so stepping through the
    // summary never changes which marker counts as open.
    const [newest] = events;

    return (
      <EventMarker
        key={`event-${newest.id}`}
        events={events}
        position={position}
        summaryOpen={newest.id === openEventId}
        onSelect={() => handleSelect(newest.id)}
        onClose={handleClose}
      />
    );
  });
}
